import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import {
  defaultCellRenderer,
  defaultCellRenderer as renderer,
  defaultGroupRenderer as groupRenderer,
  showToast,
  showConfirmationDialog,
  copyTextToClipboard
} from "../../../other/utils/utils.jsx";
import { DateHelper, MessageDialog } from "@bryntum/schedulerpro";
import SwivuFilesStore from "../../stores/SwivuFilesStore.jsx";
import {
  SwivuDocumentType,
  SWIVU_ASSIST_CONFIG
} from "../../../other/constants/OtherKeys.jsx";
import { cellMenuItemRemoveRow } from "../../../other/utils/gridCellMenuUtils.jsx";
import { uploadFile } from "../../../network/network.jsx";
import {
  assignViewsState,
  debounce
} from "../../../other/utils/otherUtils.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import { updateTabTitleWithRecordCount } from "../../../other/utils/editorUtils.jsx";
import { SwivuStoreCanViewMixin } from "../../stores/mixins/SwivuStoreCanViewMixin.jsx";
import { downloadFileFromUrl } from "../../../other/utils/domUtils.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { SwivuCompanyCamGalleryView } from "@/bryntum-lib/editors/swivuCompanyCamProjectsEditor/SwivuCompanyCamGalleryView.jsx";

const EXCLUDED_FILTER_ID = "excluded-sources";

/* Todo: Fix toggle of grid view
The way we are handling toggle of the grid view is not a best practice, since we are using css classes to hide the grid. Initially we were using grid.hide() to hide the grid, but we needed to show grids toolbar in the gallery view as well. So we tried creating duplicate toolbar using configurable but the file upload button's button config was not working inside a 'container' widget being used as a toolbar. So we had to move to utilizing the existing toolbar and toggling the grid's visibility using tailwind. */

const GRID_HIDDEN_CLASS = [
  "[&_.b-grid-panel-body]:!hidden",
  "!h-auto",
  "!min-h-[auto]",
  "flex-[0_0_auto]",
  "[&_.b-mask]:!hidden"
];

const GRID_BUTTON_CONFIG = {
  grid: {
    icon: "fa-thin fa-list",
    tooltip: {
      cls: "swivu-modern-tooltip",
      html: "Switch to Grid View"
    }
  },
  gallery: {
    icon: "fa-thin fa-grid ml-[2px]",
    tooltip: {
      cls: "swivu-modern-tooltip",
      html: "Switch to Gallery View"
    }
  }
};

const canEditOrViewRenderer = (renderData) => {
  if (renderData.record.source === SwivuDocumentType.ccDocument) {
    return "N/A";
  }
  return renderData.column.defaultRenderer(renderData);
};

const documentRowExpanderColumns = [
  {
    id: "description-detail",
    field: "description",
    text: "Description",
    flex: 1,
    editor: true,
    renderer: ({ value }) => defaultCellRenderer({ value })
  },
  {
    id: "size",
    field: "size",
    text: "Size",
    flex: 1,
    editor: false
  },
  {
    id: "last-update",
    flex: 1,
    text: "Last Update",
    field: "lastUpdatedAt",
    editor: false,
    renderer(e) {
      return e.value
        ? DateHelper.format(new Date(e.value), "MM/DD/YYYY")
        : renderer({ value: null });
    }
  },
  {
    id: "last-updated-by",
    flex: 1,
    text: "Last Updated By",
    field: "lastUpdatedBy",
    editor: false,
    renderer
  }
];

const documentColumns = [
  {
    id: "name",
    field: "name",
    text: "Name",
    width: 180,
    type: WIDGET_KEYS.swivuSortColumn,
    editor: false,
    renderer: "up.nameColumnRenderer",
    groupRenderer
  },
  {
    id: "added-by",
    field: "addedBy",
    text: "Added By",
    flex: 1,
    editor: false,
    renderer,
    groupRenderer
  },
  {
    id: "can-edit",
    field: "canEdit",
    text: "Can Edit",
    type: WIDGET_KEYS.swivuNoteChangeRoleColumn,
    renderer: canEditOrViewRenderer
  },
  {
    id: "can-view",
    field: "canView",
    text: "Can View",
    type: WIDGET_KEYS.swivuNoteChangeRoleColumn,
    finalizeCellEdit: "up.onFinalizeCellEdit",
    renderer: canEditOrViewRenderer
  },
  {
    id: "aging",
    field: "aging",
    text: "Age",
    flex: 1,
    editor: false,
    renderer(e) {
      return DateHelper.formatCount(e.value, "day");
    },
    groupRenderer({ groupRowFor, count }) {
      return groupRowFor
        ? groupRenderer({
            groupRowFor: DateHelper.formatCount(groupRowFor, "day"),
            count
          })
        : defaultCellRenderer({ value: null });
    }
  }
];

export const HasFileBaseMixin = (base) =>
  class extends base {
    static get $name() {
      return "HasFileBaseMixin";
    }

    get swivuDeepLinkSlug() {
      return "files";
    }

    static get configurable() {
      return {
        title: "Files",
        tab: {
          icon: "fa-regular fa-image w-4 h-4"
        },
        layout: "vbox",
        cls: "gap-[1em]",
        items: {
          dataGrid: {
            swivuGridTitle: "Files",
            height: "100%",
            type: WIDGET_KEYS.swivuModernGrid,
            columns: documentColumns,
            features: {
              rowExpander: {
                dataField: "documentDetails",
                columnPosition: "first",
                column: {
                  id: "document-row-expander",
                  draggable: false,
                  minWidth: 50
                },
                widget: {
                  ref: "documentDetailGrid",
                  type: WIDGET_KEYS.swivuModernGrid,
                  autoHeight: true,
                  columns: documentRowExpanderColumns,
                  features: {
                    sort: false,
                    filter: false,
                    group: false,
                    cellMenu: false
                  },
                  listeners: {
                    beforeCellEditStart: "up.onBeforeCellEditStart"
                  }
                }
              },
              cellMenu: {
                processItems({ items, record }) {
                  items.removeRow.hidden =
                    record.source === SwivuDocumentType.ccDocument ||
                    record.isGroupHeader;
                  items.downloadCCDoc.hidden =
                    record.source !== SwivuDocumentType.ccDocument ||
                    record.isGroupHeader;
                },
                items: {
                  cut: false,
                  copy: false,
                  paste: false,
                  removeRow: cellMenuItemRemoveRow({
                    icon: "fa-regular fa-trash !text-danger",
                    onItem: "up.onDeleteRecord"
                  }),
                  copyLink: {
                    icon: "fa-regular fa-copy",
                    onItem: "up.onCopyFileLink",
                    text: "Copy Link"
                  },
                  downloadCCDoc: {
                    text: "Download",
                    icon: "fa-light fa-arrow-down-to-line",
                    onItem: "up.onDownloadDocFile"
                  }
                }
              }
            },
            selectionMode: {
              multiSelect: false
            },
            listeners: {
              beforeCellEditStart: "up.onBeforeCellEditStart",
              finishCellEdit: "up.onCellEditFinish",
              cellDblClick: "up.onCellDoubleClickHandler",
              beforeStateSave: "up.onBeforeStateSave"
            },
            tbar: {
              items: {
                viewToggleButton: {
                  type: "button",
                  cls: "!text-[1.6em] border-0 p-0 b-transparent min-h-[auto]",
                  onClick: "up.onToggleView"
                },
                separator: {
                  type: "displayField",
                  cls: "swivu-vertical-separator"
                },
                uploadDocumentsButton: {
                  type: "filePicker",
                  cls: "flex items-center justify-center",
                  fileFieldConfig: { multiple: true, accept: "*/*" },
                  buttonConfig: {
                    type: "button",
                    icon: "fa-light fa-arrow-up-from-bracket",
                    text: "Upload Files",
                    cls: "swivu-modern-button b-blue b-raised"
                  },
                  onChange: "up.onUploadDocuments"
                }
              }
            }
          },
          galleryView: {
            type: SwivuCompanyCamGalleryView.type
          }
        },
        swivuAssistance: SWIVU_ASSIST_CONFIG.files
      };
    }

    loadRecord(record, context, otherParams) {
      super.loadRecord(record, context, otherParams);
      this.record = record;

      if (!otherParams.isFirstShow) return;

      const { dataGrid } = this.widgetMap;

      dataGrid.columns.getById("can-edit").onLoad(context.project, { context });
      dataGrid.columns.getById("can-view").onLoad(context.project, { context });

      const companyCamProjectRecord = context.project
        .getCrudStore(STORE_KEYS.extraBoardIntegrationsStore)
        .find((item) => item.key === "companyCam");

      dataGrid.store = new (SwivuStoreCanViewMixin(
        SwivuFilesStore,
        context.user,
        "canView",
        this.swivuCanViewFilter
      ))({
        swivuProject: context.project,
        swivuEntityRecord: record,
        swivuEntityRecordType: this.recordType,
        swivuActiveEntitiesFn() {
          return {
            isActiveCCEntity: !!companyCamProjectRecord
          };
        }
      });
      dataGrid.store.crudManager = context.project;
      dataGrid.stateId = this.stateId;
      assignViewsState([dataGrid], this.stateProvider);

      this.checkEntityCCUpdate();

      this.swivuLoadData();

      if (this.swivuDocumentGridUpdateListener) {
        this.swivuDocumentGridUpdateListener();
      }
      this.swivuDocumentGridUpdateListener = updateTabTitleWithRecordCount(
        this,
        dataGrid.store
      );

      this.onToggleView();
    }

    checkEntityCCUpdate() {
      this.swivuEntityLastCompanyCamId = this.record.companyCamProject;
      if (!this.swivuEntityIntegratioUpdateListener) {
        this.swivuEntityIntegratioUpdateListener = this.project
          .getCrudStore(this.storeKey)
          .on({
            update({ record: entityCCIntRecord }) {
              if (
                entityCCIntRecord.value !== this.swivuEntityLastCompanyCamId
              ) {
                this.swivuEntityLastCompanyCamId = entityCCIntRecord.value;
                this.swivuLoadData();
              }
            },
            remove(e) {
              if (e.records.length) {
                const ccRecord = e.records[0];
                if (
                  ccRecord[this.recordType] === this.record.id &&
                  ccRecord.key === "companyCamProject"
                ) {
                  this.swivuEntityLastCompanyCamId = null;
                  this.swivuLoadData(true);
                }
              }
            },
            thisObj: this
          });
      }
    }

    swivuCanViewFilter(record, user, field) {
      // CC Document doesn't have permission fields so allow it to be viewed
      if (
        !record.originalRecordId ||
        record.source === SwivuDocumentType.ccDocument
      ) {
        return true;
      }
      return user.can(record[field]);
    }

    onBeforeStateSave(e) {
      if (e.state?.store?.filters?.length) {
        // prevent saving tbar filters
        e.state.store.filters = e.state.store.filters.filter(
          (filter) => filter.id !== EXCLUDED_FILTER_ID
        );
      }
    }

    onFinalizeCellEdit({ value, record }) {
      if (value < record.canEdit) {
        return "View permission cannot be higher than Edit permission";
      }
      return true;
    }

    onCellEditFinish({ editorContext: { record } }) {
      if (record.canView < record.canEdit) {
        record.canView = record.canEdit;
      }
    }

    displayPermissionNotAllowed(keyMsg) {
      const currentRole = this.boardManager.selectedBoard.record.roleNames;
      MessageDialog.alert({
        title: "Permission Denied!",
        message: `Your role is "${currentRole}", and You don't have permission to ${keyMsg} this record.`
      });
    }

    onUploadDocuments({ files }) {
      const { uploadDocumentsButton, dataGrid } = this.widgetMap;
      dataGrid.mask("Loading...");
      const promises = [...files].map((file) => uploadFile(file, file.name));
      const allPromises = Promise.allSettled(promises)
        .then((response) => {
          const recordsToAdd = response.reduce((prevRecords, promiseResult) => {
            if (promiseResult.status === "fulfilled") {
              const { data } = promiseResult.value;
              prevRecords.push({
                documentId: data.id,
                [this.recordType]: this.record.id
              });
            } else {
              showToast({
                type: "error",
                msg: promiseResult.reason.response?.data?.message
                  ? promiseResult.reason.response.data.message
                  : "The file upload encountered an issue. Please contact support for assistance."
              });
            }
            return prevRecords;
          }, []);
          this.project
            .getCrudStore(STORE_KEYS.boardFilesStore)
            .add(recordsToAdd);
        })
        .finally(() => {
          dataGrid.unmask();
          uploadDocumentsButton.clear();
        });

      if (this.currentView === "gallery") {
        allPromises.then(() => {
          const detacher = this.project
            .getCrudStore(STORE_KEYS.boardFilesStore)
            .on({
              update: debounce(() => {
                this.reloadGalleryView();
                detacher();
              }),
              thisObj: this
            });
        });
      }
    }

    onDownloadDocFile({ record }) {
      const { dataGrid } = this.widgetMap;
      downloadFileFromUrl(
        record.originalImageUrl || record.url,
        record.name
      ).finally(() => dataGrid.unmask());
    }

    onDeleteFileRecord(recordsToRemove) {
      if (this.user.can(recordsToRemove.canEdit)) {
        showConfirmationDialog({
          title: "Delete File",
          message: `Are you sure you want to delete "${defaultCellRenderer({
            value: recordsToRemove?.name
          })}" file?`,
          cancelBtnText: "Cancel",
          confirmBtnText: "Delete",
          isCloseByHeader: true
        }).then((isDelete) => {
          if (isDelete) {
            this.project
              .getCrudStore(STORE_KEYS.boardFilesStore)
              .remove(recordsToRemove.originalRecordId);
          }
        });
      }
    }

    swivuLoadData(isReload = true) {
      const { dataGrid } = this.widgetMap;
      dataGrid.mask("Loading...");
      dataGrid.store
        .swivuGetData(isReload)
        .then((record) => {
          dataGrid.store.data = record.data;
        })
        .finally(() => {
          dataGrid.unmask();
        });
    }

    onTabChangeHandler({ activeItem }) {
      const { dataGrid } = this.widgetMap;
      if (activeItem.type === this.type) {
        dataGrid.store.swivuAttachUpdateListenersOnOriginalStores();
        this.swivuLoadData(false);
      } else if (!dataGrid.store.isDestroyed) {
        dataGrid.store.swivuDetachUpdateListenersFromOriginalStores();
      }

      if (!this.debouncedReloadGalleryView) {
        this.debouncedReloadGalleryView = debounce(() => {
          this.reloadGalleryView();
        }, 500);
      }
      if (
        activeItem.type === this.type &&
        this.currentView === "gallery" &&
        this.otherParams &&
        !this.otherParams.isFirstShow
      ) {
        this.debouncedReloadGalleryView();
      }
    }

    onSwivuEditorHide() {
      super.onSwivuEditorHide();

      const { dataGrid } = this.widgetMap;

      if (this.swivuDocumentGridUpdateListener) {
        this.swivuDocumentGridUpdateListener();
        this.swivuDocumentGridUpdateListener = null;
      }

      if (this.swivuEntityIntegratioUpdateListener) {
        this.swivuEntityIntegratioUpdateListener();
        this.swivuEntityIntegratioUpdateListener = null;
      }

      if (!dataGrid.store.isDestroyed) {
        dataGrid.store.swivuDetachUpdateListenersFromOriginalStores();
      }
    }

    toggleDataGrid(show = true) {
      if (show) {
        this.widgetMap.dataGrid.element.classList.remove(...GRID_HIDDEN_CLASS);
      } else {
        this.widgetMap.dataGrid.element.classList.add(...GRID_HIDDEN_CLASS);
      }
    }

    reloadGalleryView() {
      this.widgetMap.galleryView.loadRecord(
        this.record,
        { project: this.project },
        { ...this.otherParams, forceRefresh: true }
      );
    }

    onToggleView() {
      const { galleryView, viewToggleButton } = this.widgetMap;

      if (this.currentView === "gallery") {
        this.toggleDataGrid(false);
        galleryView.show();
        this.currentView = "grid";
        this.reloadGalleryView();
        viewToggleButton.icon = GRID_BUTTON_CONFIG.grid.icon;
        viewToggleButton.tooltip = GRID_BUTTON_CONFIG.grid.tooltip;
      } else {
        this.toggleDataGrid();
        galleryView.hide();
        this.currentView = "gallery";
        viewToggleButton.icon = GRID_BUTTON_CONFIG.gallery.icon;
        viewToggleButton.tooltip = GRID_BUTTON_CONFIG.gallery.tooltip;
      }
    }

    onCellDoubleClickHandler({ record, column }) {
      if (column.id === "name" && record.originalRecordId) {
        if (record.source === SwivuDocumentType.swivuFile) {
          const fileOriginalRecord = this.project
            .getCrudStore(STORE_KEYS.boardFilesStore)
            .getById(record.originalRecordId);
          window.open(fileOriginalRecord.url, "_blank");
        } else if (record.source === SwivuDocumentType.ccDocument) {
          window.open(record.originalImageUrl || record.url, "_blank");
        }
      }
    }

    swivuCheckGrants(user) {
      if (!user.can(GRANTS.grant_four)) {
        const { dataGrid } = this.widgetMap;
        dataGrid.tbar.widgetMap.uploadDocumentsButton.disabled = true;
      }
    }

    onCopyFileLink({ record }) {
      copyTextToClipboard(
        record.url || record.originalImageUrl,
        `Link copied.`
      );
    }

    onDeleteRecord() {
      const { dataGrid } = this.widgetMap;
      const recordToRemove = dataGrid.selectedRecord;

      if (recordToRemove) {
        if (recordToRemove.source === SwivuDocumentType.swivuFile) {
          this.onDeleteFileRecord(recordToRemove);
        }
      }
    }

    // prevent editing CC records
    onBeforeCellEditStart({ editorContext }) {
      const record = editorContext.record;
      if (record.source === SwivuDocumentType.swivuFile) {
        return record.originalRecordId && this.user.can(record.canEdit);
      }
      return false;
    }

    nameColumnRenderer({ value, record }) {
      return {
        tag: "div",
        className: "flex items-center gap-[0.75em]",
        children: [
          {
            tag: "img",
            class: "w-8 h-8 rounded object-cover",
            onError:
              "this.src='https://www.iconpacks.net/icons/2/free-file-icon-1453-thumb.png'",
            src: record.thumbnailImageUrl || record.url,
            alt: value || "image"
          },
          {
            tag: "span",
            class: "text-black dark:text-white text-[0.875em] font-semibold",
            text: value || record.name || "Untitled"
          }
        ]
      };
    }
  };
